import React from "react";
import { Box, Typography } from "@mui/material";
import { DetailItem } from "../../../components/ModalDetailsGeneric/ModalDetailsGeneric";
import {
  EMPTY_VALUE,
  TitleValue,
  STATE_LABELS,
  dash,
  getStateKeys,
  buildRequestNumberItem,
  buildStateLabelItem,
} from "../../../components/ModalDetailsGeneric/detailItemsShared";
import { applyVolumeMask } from "../../../utils";

interface OrderHeader {
  distributor?: TitleValue;
  requestNumber?: TitleValue;
  subsidiaryDescription?: TitleValue;
  requestDate?: TitleValue;
}

interface OrderContentLabels {
  productName?: TitleValue;
  modality?: TitleValue;
  quantity?: TitleValue;
}

interface OrderStateValues {
  productName?: string;
  modality?: string;
  quantity?: string;
  measurementUnit?: string;
}

export interface OrderModalData extends OrderStateValues {
  header: OrderHeader;
  content: OrderContentLabels;
  status?: string;
  step?: string;
  // Per-state values (e.g. approved / orded) present when withTab is true.
  [key: string]: unknown;
}

function formatQuantity(state: OrderStateValues): string {
  const hasQuantity = state.quantity != null && state.quantity.trim() !== "";
  if (!hasQuantity) return EMPTY_VALUE;
  const value = applyVolumeMask(String(state.quantity)) || "0";
  const unit = state.measurementUnit ?? "L";
  return `${value} ${unit}`;
}

function buildStateFields(
  content: OrderContentLabels,
  state: OrderStateValues
): DetailItem[] {
  return [
    {
      label: content.productName?.title ?? "Nome do produto",
      value: dash(state.productName),
    },
    {
      label: content.modality?.title ?? "Modalidade",
      value: dash(state.modality),
    },
    {
      label: content.quantity?.title ?? "Quantidade",
      value: formatQuantity(state),
    },
  ];
}

function totalItem(state: OrderStateValues, prefix?: string): DetailItem {
  const label = prefix ? `Total do pedido (${prefix})` : "Total do pedido";
  const hasQuantity = state.quantity != null && state.quantity.trim() !== "";

  if (!hasQuantity) {
    return { label, fullWidth: true, value: EMPTY_VALUE };
  }

  return {
    label,
    fullWidth: true,
    value: (
      <Box display="flex" alignItems="baseline" gap={0.5}>
        <Typography fontSize={20} fontWeight={700} color="shadesOfDark.dark">
          {applyVolumeMask(String(state.quantity)) || "0"}
        </Typography>
        <Typography fontSize={13} color="shadesOfDark.dark">
          {state.measurementUnit ?? "L"}
        </Typography>
      </Box>
    ),
  };
}

/**
 * Builds the flat DetailItem[] for the quota order detail modal so it can be
 * rendered by the shared DetailsModal (ModalDetailsGeneric), matching the Notas
 * Fiscais modal design.
 *
 * Field mapping:
 * - header.requestNumber / subsidiaryDescription / requestDate -> DetailItems
 * - productName / modality / quantity -> DetailItems (per approved/orded state
 *   when withTab is true, otherwise the top-level values)
 * - Total do pedido -> full-width highlighted DetailItem (per state when tabbed)
 */
export function buildQuotaOrderDetailItems(
  data: OrderModalData,
  withTab: boolean,
  statusNode?: React.ReactNode
): DetailItem[] {
  const { header, content } = data;
  const items: DetailItem[] = [];

  items.push(
    buildRequestNumberItem(
      header?.requestNumber?.title ?? "N. do pedido",
      header?.requestNumber?.value,
      statusNode
    )
  );
  items.push({
    label: header?.subsidiaryDescription?.title ?? "Filial",
    value: dash(header?.subsidiaryDescription?.value),
  });
  items.push({
    label: header?.requestDate?.title ?? "Data do pedido",
    value: dash(header?.requestDate?.value),
  });

  const stateKeys = getStateKeys(data);

  if (withTab && stateKeys.length > 0) {
    stateKeys.forEach((key) => {
      const state = data[key] as OrderStateValues;
      items.push(buildStateLabelItem(key));
      items.push(...buildStateFields(content, state));
      items.push(totalItem(state, STATE_LABELS[key] ?? key));
    });
  } else {
    items.push(...buildStateFields(content, data));
    items.push(totalItem(data));
  }

  return items;
}
